/**
 * Agents hub (`/agents`) — the landing page: a lead line, a card grid with one
 * card per scheduled agent type of the registry (merged with the brand's
 * activated agents by `agent_type`), and a right-hand drawer per agent with
 * its real outputs and recent runs. Modelled on the Demo 2.0 Agents page.
 *
 * Everything is scoped to the GLOBAL active brand (switched from the
 * bottom-left). The full page (runs, drawer) needs `portal.reports.review`.
 * A member with only `skills.view` gets the cards alone: the agent list is
 * the one request the server answers for them, so no runs are loaded and the
 * cards do not open. The drawer is
 * read-only — Configure navigates to the per-agent config page
 * (`/agents/:agentType`), which owns activation, schedule, settings and Run
 * now. `?agent=<type>` opens a drawer on load, and Configure returns to that
 * URL so the panel the user came from is open again.
 *
 * Data: one `listAgents` + one `listAgentRuns(limit 20)` per brand, grouped
 * by type client-side (no per-agent fan-out); runs re-poll every 10s only
 * while one is in flight (contract cadence).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import { useCan } from '../../../lib/rbac';
import { setMessage } from '../../../store/reducers/appSlice';
import type { RootState } from '../../../store/store';
import {
  deleteCustomAgent,
  listAgentRuns,
  listAgents,
  type AgentView,
  type RunView,
  type TypeView,
} from '../../../lib/api/client-reports/client-agents-api';
import { shouldPollRuns } from '../../client-reports/agents/agent-helpers';
import AgentCardTile from './AgentCardTile';
import AgentDrawer from './AgentDrawer';
import AgentsTabs from './AgentsTabs';
import BuilderWizardPopup from './BuilderWizardPopup';
import Popup from '../../../ui/popup/Popup';
import '../../../styles/pages/agents/agentsHub.scss';

/** How often the runs re-poll while one is running/generating (contract: 10s). */
const POLL_INTERVAL_MS = 10000;
/** Enough recent runs to cover "last run" per card + the drawer's scrolling list. */
const RUNS_WINDOW = 20;

/** The drawer's deep link: `/agents?agent=<agent_type>` (the config page and
 *  the chat view return here so the panel the user came from is open again). */
export const agentDrawerPath = (agentType: string) =>
  `/agents?agent=${encodeURIComponent(agentType)}`;

const AgentsHub = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const canRead = useCan('portal.reports.review');
  const canViewSkills = useCan('skills.view');
  /** May load the agent list (the cards). */
  const canList = canRead || canViewSkills;
  /** Cards without runs, outputs or a drawer: those need `canRead`. */
  const cardsOnly = canList && !canRead;
  const canManage = useCan('portal.settings.manage');

  const activeShopSlug = useSelector(
    (s: RootState) => s.user?.profile?.active_shop_slug || ''
  );
  const activeShopName = useSelector(
    (s: RootState) =>
      (s.user?.profile?.all_shops ?? []).find(
        (shop: any) => shop?.slug === s.user?.profile?.active_shop_slug
      )?.name || ''
  );

  const [agents, setAgents] = useState<AgentView[]>([]);
  const [types, setTypes] = useState<TypeView[]>([]);
  const [runs, setRuns] = useState<RunView[]>([]);
  /** Brand whose data is actually in state — guards cross-brand renders. */
  const [loadedSlug, setLoadedSlug] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** The 'Build your own agent' wizard (a popup over the hub). */
  const [wizardOpen, setWizardOpen] = useState<boolean>(
    () => !!(location.state as { openWizard?: boolean } | null)?.openWizard
  );
  /** The custom agent being edited in the popup (definition id), or null = create. */
  const [wizardEditId, setWizardEditId] = useState<string | null>(
    () => (location.state as { editId?: string } | null)?.editId ?? null
  );
  /** Remove a custom agent from its card: the confirm step, then the request. */
  const [removeType, setRemoveType] = useState<TypeView | null>(null);
  const [removing, setRemoving] = useState(false);
  /** The open drawer's agent type (by id so it survives a re-fetch). Seeded
   *  from `?agent=` so Configure / chat can send the user back to the panel. */
  const [openType, setOpenTypeState] = useState<string | null>(
    () => new URLSearchParams(location.search).get('agent') || null
  );
  const setOpenType = useCallback(
    (agentType: string | null) => {
      setOpenTypeState(agentType);
      // Keep the URL in step so the drawer survives a reload / back button.
      navigate(agentType ? agentDrawerPath(agentType) : '/agents', {
        replace: true,
      });
    },
    [navigate]
  );
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(
    async (slug: string) => {
      if (!slug) return;
      setLoading(true);
      setLoadError(null);
      try {
        // The run history is refused without `portal.reports.review`.
        const [index, page] = await Promise.all([
          listAgents(slug),
          canRead ? listAgentRuns(slug, { limit: RUNS_WINDOW }) : null,
        ]);
        setAgents(index.agents);
        setTypes(index.available_types);
        setRuns(page ? page.data : []);
        setLoadedSlug(slug);
      } catch (err: any) {
        setAgents([]);
        setTypes([]);
        setRuns([]);
        setLoadedSlug(null);
        // A 403 means the brand is outside the viewer's scope — no toast,
        // the page explains it. Anything else is a real failure.
        if (err?.status === 403) {
          setLoadError(
            t(
              'AgentsNoBrandAccess',
              "You don't have access to this brand's agents. Pick another brand from the bottom-left."
            )
          );
        } else {
          const message =
            err?.message ||
            t('AgentsLoadFailed', 'Failed to load the agents for this brand.');
          setLoadError(message);
          dispatch(setMessage({ message, messageType: 'error' }));
        }
      } finally {
        setLoading(false);
      }
    },
    // `t` is left out: a language switch must not re-fetch the agents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, canRead]
  );

  useEffect(() => {
    if (canList && activeShopSlug) load(activeShopSlug);
  }, [canList, activeShopSlug, load]);

  useEffect(() => {
    if ((location.state as { openWizard?: boolean } | null)?.openWizard) {
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeCustom = async () => {
    if (!removeType?.definition_id || removing) return;
    setRemoving(true);
    try {
      const result = await deleteCustomAgent(removeType.definition_id);
      dispatch(
        setMessage({
          message:
            result.failed > 0
              ? t(
                  'AgentsRemovedPartly',
                  '{{agent}} removed — {{failed}} brand(s) could not be switched off; AdUp has been notified.',
                  { agent: removeType.label, failed: result.failed }
                )
              : t('AgentsRemoved', '{{agent}} removed.', {
                  agent: removeType.label,
                }),
          messageType: result.failed > 0 ? 'info' : 'success',
        })
      );
      setRemoveType(null);
      if (openType === removeType.agent_type) setOpenType(null);
      load(activeShopSlug);
    } catch (err: any) {
      dispatch(
        setMessage({
          message:
            err?.message ||
            t('AgentsRemoveFailed', 'Failed to remove the agent.'),
          messageType: 'error',
        })
      );
    } finally {
      setRemoving(false);
    }
  };

  // Poll the runs (only) while one is in flight.
  const isPolling =
    canRead && loadedSlug === activeShopSlug && shouldPollRuns(runs);
  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (!isPolling || !activeShopSlug) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const [index, page] = await Promise.all([
          listAgents(activeShopSlug),
          listAgentRuns(activeShopSlug, { limit: RUNS_WINDOW }),
        ]);
        setAgents(index.agents);
        setRuns(page.data);
      } catch {
        /* keep the last good state; the next tick retries */
      }
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [isPolling, activeShopSlug]);

  const agentByType = useMemo(() => {
    const map: Record<string, AgentView> = {};
    agents.forEach((a) => {
      map[a.agent_type] = a;
    });
    return map;
  }, [agents]);

  const runsByType = useMemo(() => {
    const map: Record<string, RunView[]> = {};
    runs.forEach((r) => {
      (map[r.agent_type] ||= []).push(r);
    });
    return map;
  }, [runs]);

  // Only the schedulable types get cards (a chat-kind registry entry has no
  // card and no button — Agents v3 removed "Chat with Agent").
  const cardTypes = useMemo(
    () => types.filter((type) => type.kind !== 'chat'),
    [types]
  );
  const openTypeView = useMemo(
    () => (openType ? types.find((type) => type.agent_type === openType) ?? null : null),
    [openType, types]
  );

  const ready = !loading && loadedSlug === activeShopSlug && !loadError;

  return (
    <SubPageInnerContentHolder
      title={t('Agents', 'Agents')}
      backgroundColor={'var(--main-background-color)'}
    >
      <div className='agentsHub'>
        <AgentsTabs active='agents' showSkills={canManage} />
        <p className='agentsHub__lead'>
          {cardsOnly ? (
            t(
              'AgentsLeadCardsOnly',
              'Managed AI agents that run for you on a schedule — reporting, KPI workbooks and more. Opening an agent\'s output and settings needs the "review reports" permission.'
            )
          ) : (
            <>
              {t(
                'AgentsLeadBefore',
                'Managed AI agents that run for you on a schedule — reporting, KPI workbooks and more. Click an agent to see what it does and open its'
              )}{' '}
              <strong>{t('AgentsLeadActualOutput', 'actual output')}</strong>.
            </>
          )}
        </p>

        {!canList ? (
          <div className='agentsHub__empty'>
            {t(
              'AgentsNoPermission',
              "You don't have permission to view agents. Ask an owner or manager for access."
            )}
          </div>
        ) : !activeShopSlug ? (
          <div className='agentsHub__empty'>
            {t(
              'AgentsNoActiveBrand',
              'No active brand. Pick one from the button in the bottom-left.'
            )}
          </div>
        ) : (
          <>
            <p className='agentsHub__brand'>
              {t('AgentsShowingFor', 'Showing agents for')}{' '}
              <strong>{activeShopName || activeShopSlug}</strong>{' '}
              {t('AgentsSwitchBrand', '· switch brand from the bottom-left.')}
            </p>

            {loading || (!loadError && loadedSlug !== activeShopSlug) ? (
              <div style={{ marginTop: '3rem', textAlign: 'center' }}>
                <LoadingSpinner customLoadingMsg={t('AgentsLoading', 'Loading agents…')} />
              </div>
            ) : loadError ? (
              <div className='agentsHub__error'>
                {loadError}
                <div style={{ marginTop: '1.4rem' }}>
                  <MainSquareButton
                    type='button'
                    value={t('Retry', 'Retry')}
                    buttonType='light'
                    normal
                    onClick={() => load(activeShopSlug)}
                  />
                </div>
              </div>
            ) : cardTypes.length === 0 ? (
              <div className='agentsHub__empty'>
                {t(
                  'AgentsNoneAvailable',
                  'No agents are available for your organisation yet.'
                )}
              </div>
            ) : (
              <div className='agentsHub__grid' data-testid='agents-grid'>
                {cardTypes.map((type) => (
                  <AgentCardTile
                    key={type.agent_type}
                    type={type}
                    agent={agentByType[type.agent_type] ?? null}
                    lastRun={runsByType[type.agent_type]?.[0] ?? null}
                    onOpen={
                      cardsOnly
                        ? undefined
                        : (opened) => setOpenType(opened.agent_type)
                    }
                    canSetUp={canManage}
                    onRemove={
                      canManage && type.custom
                        ? (removed) => setRemoveType(removed)
                        : undefined
                    }
                  />
                ))}
                {canManage && (
                  <button
                    type='button'
                    className='agentsHub__card agentsHub__card--build'
                    data-testid='agent-card-build'
                    onClick={() => {
                      setWizardEditId(null);
                      setWizardOpen(true);
                    }}
                  >
                    <div className='agentsHub__card-top'>
                      <span className='agentsHub__eyebrow'>{t('AgentsBuildOwnEyebrow')}</span>
                    </div>
                    <h3>{t('AgentsBuildOwnTitle')}</h3>
                    <p>{t('AgentsBuildOwnBody')}</p>
                    <div className='agentsHub__card-meta'>
                      <span>{t('AgentsBuildOwnRuns')}</span>
                      <span>{t('AgentsBuildOwnReadOnly')}</span>
                    </div>
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {canManage && (
          <BuilderWizardPopup
            open={wizardOpen}
            shopSlug={activeShopSlug}
            brandName={
              activeShopName ||
              activeShopSlug ||
              t('AgentThisBrand', 'this brand')
            }
            editDefinitionId={wizardEditId}
            onClose={() => {
              setWizardOpen(false);
              setWizardEditId(null);
            }}
            onCreated={({ agentType, show }) => {
              setWizardOpen(false);
              setWizardEditId(null);
              load(activeShopSlug);
              if (show) setOpenType(agentType);
            }}
          />
        )}

        {/* ---- remove a custom agent (from its card) ---- */}
        <Popup trigger={removeType !== null} setTrigger={() => { if (!removing) setRemoveType(null); }}>
          {removeType && (
            <div className='client-agents__confirm' data-testid='remove-agent-confirm'>
              <h3 className='client-agents__confirm-title'>
                {t('AgentsRemoveConfirmTitle', 'Remove {{agent}}?', {
                  agent: removeType.label,
                })}
              </h3>
              <p className='client-agents__confirm-body'>
                {t(
                  'AgentsRemoveConfirmBody',
                  'It is switched off for every brand that uses it and disappears from Agents. Past runs and their outputs are kept.'
                )}
              </p>
              <div className='client-agents__confirm-actions'>
                <MainSquareButton type='button' value={t('Cancel', 'Cancel')} buttonType='light' normal disabled={removing} onClick={() => setRemoveType(null)} />
                <MainSquareButton
                  type='button'
                  value={
                    removing
                      ? t('AgentTemplateRemoving', 'Removing…')
                      : t('AgentsYesRemove', 'Yes, remove')
                  }
                  buttonType='danger'
                  normal
                  loading={removing}
                  disabled={removing}
                  onClick={() => void removeCustom()}
                />
              </div>
            </div>
          )}
        </Popup>

        {ready && canRead && openTypeView && (
          <AgentDrawer
            type={openTypeView}
            agent={agentByType[openTypeView.agent_type] ?? null}
            runs={runsByType[openTypeView.agent_type] ?? []}
            brandName={activeShopName}
            canManage={canManage}
            onClose={() => setOpenType(null)}
            onDeleted={() => {
              setOpenType(null);
              load(activeShopSlug);
            }}
            onEdit={(edited) => {
              if (!edited.definition_id) return;
              setOpenType(null);
              setWizardEditId(edited.definition_id);
              setWizardOpen(true);
            }}
          />
        )}
      </div>
    </SubPageInnerContentHolder>
  );
};

export default AgentsHub;
